import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VolumeReader } from "@/components/reloc/VolumeReader";
import { RelocDownloads } from "@/components/reloc/RelocDownloads";
import { RelocListing } from "@/components/reloc/RelocListing";
import { RelocHistory, RelocSources } from "@/components/reloc/RelocPanels";
import { RelocHeader, RelocActions } from "@/components/reloc/RelocTop";
import { RelocAccuracy, RelocRetries, RelocIllustrations, RelocInstagramPost } from "@/components/reloc/RelocSections";
import { BuildProgress } from "@/components/reloc/BuildProgress";
import { KitFiles } from "@/components/reloc/KitFiles";
import { RelocTools, ToolsPost } from "@/components/reloc/RelocTools";
import { RelocAppPost } from "@/components/reloc/RelocAppPost";
import { VideoPrompts } from "@/components/VideoPrompts";
import { LaunchProduct } from "@/components/LaunchProduct";

const TABS = [["volumes", "Volumes"], ["files", "Kit files"], ["tools", "Spreadsheet tools"], ["listing", "Store listing"], ["fees", "Fee updates"], ["sources", "Sources"]];

export default function RelocationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [g, setG] = useState(null);
  const load = useCallback(() => api.get(`/relocation/guides/${id}`).then((r) => setG(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!g?.running) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [g, load]);
  if (!g) return <p className="eyebrow">Loading guide…</p>;

  return (
    <div className="rise space-y-12">
      <div>
        <Link to="/relocation" data-testid="reloc-back" className="inline-flex items-center gap-2 text-sm text-[#8C827A] hover:text-[#3B1B48] mb-8"><ArrowLeft className="w-4 h-4" />All guides</Link>
        <RelocHeader g={g} />
        <RelocActions g={g} setG={setG} onDeleted={() => nav("/relocation")} onSynced={load} />
      </div>
      <BuildProgress g={g} onResumed={load} />
      {["ready", "published"].includes(g.status) && !g.running && <LaunchProduct path={`/launch/reloc/${g.id}`} what="relocation guide" onChanged={load} />}
      <RelocAccuracy g={g} setG={setG} onStarted={load} />
      <RelocRetries g={g} />
      <RelocIllustrations g={g} onStarted={load} />
      <section className="space-y-6" data-testid="reloc-instagram-section">
        <p className="eyebrow">Instagram posts</p>
        <RelocInstagramPost g={g} />
        <RelocAppPost g={g} />
        {Object.keys(g.files).some((k) => k.startsWith("tool_")) && <div data-testid="reloc-tools-post"><p className="eyebrow mb-3">Spreadsheet Tools · Instagram</p><ToolsPost g={g} /></div>}
      </section>
      <VideoPrompts kind="reloc" refId={g.id} />
      <RelocDownloads g={g} />
      <Tabs defaultValue={params.get("tab") || (g.volumes.length ? "volumes" : "files")} key={params.get("tab") || "default"}>
        <TabsList className="bg-transparent border-b border-[#E2D9CC] rounded-none w-full justify-start h-auto p-0 gap-2 mb-10 overflow-x-auto overflow-y-hidden">
          {TABS.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`reloc-tab-${v}`}
              className="rounded-none px-4 py-3 text-[#8C827A] data-[state=active]:text-[#3B1B48] data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#C59B27]">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="volumes"><VolumeReader gid={g.id} volumes={g.volumes} /></TabsContent>
        <TabsContent value="files"><KitFiles g={g} onUploaded={load} /></TabsContent>
        <TabsContent value="tools"><RelocTools g={g} onChanged={load} /></TabsContent>
        <TabsContent value="listing"><RelocListing g={g} onSaved={(listing) => setG({ ...g, listing })} /></TabsContent>
        <TabsContent value="fees"><RelocHistory g={g} /></TabsContent>
        <TabsContent value="sources"><RelocSources g={g} /></TabsContent>
      </Tabs>
    </div>
  );
}
